// Sudoku core: exact solver (counts solutions, stops at 2), seeded generator, and a human-technique
// grader. Spec: .wiki/wiki/topics/game-sudoku.md (unique solution, >= 17 givens, no guessing;
// levels mapped to technique ceilings). Grids are 81-char strings, "0" = empty.
import { seeded, shuffle } from "../lib/rng"

export type Level = "easy" | "medium" | "hard" | "expert" | "evil"
export const LEVELS: Level[] = ["easy", "medium", "hard", "expert", "evil"]

export const ROW = (i: number): number => Math.floor(i / 9)
export const COL = (i: number): number => i % 9
export const BOX = (i: number): number => Math.floor(ROW(i) / 3) * 3 + Math.floor(COL(i) / 3)

export const UNITS: number[][] = (() => {
  const u: number[][] = []
  for (let r = 0; r < 9; r++) u.push(Array.from({ length: 9 }, (_, c) => r * 9 + c))
  for (let c = 0; c < 9; c++) u.push(Array.from({ length: 9 }, (_, r) => r * 9 + c))
  for (let b = 0; b < 9; b++) {
    const r0 = Math.floor(b / 3) * 3, c0 = (b % 3) * 3
    u.push([0, 1, 2, 9, 10, 11, 18, 19, 20].map((o) => (r0 * 9 + c0) + o))
  }
  return u
})()

export const PEERS: number[][] = Array.from({ length: 81 }, (_, i) => {
  const s = new Set<number>()
  for (let j = 0; j < 81; j++) if (j !== i && (ROW(j) === ROW(i) || COL(j) === COL(i) || BOX(j) === BOX(i))) s.add(j)
  return [...s]
})

const ALL = 0x1ff
const bit = (d: number): number => 1 << (d - 1)
const popcount = (m: number): number => { let c = 0; while (m) { m &= m - 1; c++ } return c }
const digitsOf = (m: number): number[] => { const o: number[] = []; for (let d = 1; d <= 9; d++) if (m & bit(d)) o.push(d); return o }

export function parse(grid: string): number[] {
  return Array.from(grid, (ch) => (ch >= "1" && ch <= "9" ? Number(ch) : 0))
}
export const toStr = (g: number[]): string => g.join("")

/** Count solutions up to `limit` (backtracking, most-constrained cell first). Fills `out` with the first. */
export function countSolutions(grid: number[], limit = 2, out?: number[], rand?: () => number): number {
  const g = grid.slice()
  const rows = new Array(9).fill(0), cols = new Array(9).fill(0), boxes = new Array(9).fill(0)
  for (let i = 0; i < 81; i++) {
    const d = g[i]!
    if (!d) continue
    const b = bit(d)
    if (rows[ROW(i)] & b || cols[COL(i)] & b || boxes[BOX(i)] & b) return 0
    rows[ROW(i)] |= b; cols[COL(i)] |= b; boxes[BOX(i)] |= b
  }
  let count = 0
  const search = (): boolean => {
    let best = -1, bestMask = 0, bestN = 10
    for (let i = 0; i < 81; i++) {
      if (g[i]) continue
      const m = ALL & ~(rows[ROW(i)] | cols[COL(i)] | boxes[BOX(i)])
      const n = popcount(m)
      if (n < bestN) { best = i; bestMask = m; bestN = n; if (n <= 1) break }
    }
    if (best === -1) {
      count++
      if (out && count === 1) for (let i = 0; i < 81; i++) out[i] = g[i]!
      return count >= limit
    }
    let ds = digitsOf(bestMask)
    if (rand) ds = shuffle(ds, rand)
    for (const d of ds) {
      const b = bit(d)
      g[best] = d; rows[ROW(best)] |= b; cols[COL(best)] |= b; boxes[BOX(best)] |= b
      if (search()) return true
      g[best] = 0; rows[ROW(best)] &= ~b; cols[COL(best)] &= ~b; boxes[BOX(best)] &= ~b
    }
    return false
  }
  search()
  return count
}

// ---- Human-technique grader ------------------------------------------------------------------
// Tier 1: naked + hidden singles. Tier 2: + naked/hidden pairs, pointing, box-line reduction.
// Tier 3: + naked/hidden triples, X-wing. Tier 4: + swordfish, XY-wing. Tier 5: + jellyfish, XYZ-wing,
// simple coloring. Not solvable at tier 5 => rejected.

function candidates(g: number[]): number[] {
  const c = new Array(81).fill(0)
  for (let i = 0; i < 81; i++) {
    if (g[i]) continue
    let m = ALL
    for (const p of PEERS[i]!) if (g[p]) m &= ~bit(g[p]!)
    c[i] = m
  }
  return c
}

function place(g: number[], c: number[], i: number, d: number): void {
  g[i] = d; c[i] = 0
  for (const p of PEERS[i]!) c[p] = c[p]! & ~bit(d)
}

function singles(g: number[], c: number[]): boolean {
  for (let i = 0; i < 81; i++) if (!g[i] && popcount(c[i]!) === 1) { place(g, c, i, digitsOf(c[i]!)[0]!); return true }
  for (const unit of UNITS) for (let d = 1; d <= 9; d++) {
    const spots = unit.filter((i) => !g[i] && c[i]! & bit(d))
    if (spots.length === 1) { place(g, c, spots[0]!, d); return true }
  }
  return false
}

function nakedSubset(c: number[], g: number[], size: number): boolean {
  let changed = false
  for (const unit of UNITS) {
    const open = unit.filter((i) => !g[i])
    const combos = (start: number, picked: number[]): void => {
      if (picked.length === size) {
        const m = picked.reduce((a, i) => a | c[i]!, 0)
        if (popcount(m) !== size) return
        for (const i of open) if (!picked.includes(i) && c[i]! & m) { c[i] = c[i]! & ~m; changed = true }
        return
      }
      for (let k = start; k < open.length; k++) if (popcount(c[open[k]!]!) <= size) combos(k + 1, [...picked, open[k]!])
    }
    combos(0, [])
  }
  return changed
}

function hiddenSubset(c: number[], g: number[], size: number): boolean {
  let changed = false
  for (const unit of UNITS) {
    const open = unit.filter((i) => !g[i])
    const ds = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((d) => open.some((i) => c[i]! & bit(d)))
    const combos = (start: number, picked: number[]): void => {
      if (picked.length === size) {
        const cells = open.filter((i) => picked.some((d) => c[i]! & bit(d)))
        if (cells.length !== size) return
        const keep = picked.reduce((a, d) => a | bit(d), 0)
        for (const i of cells) if (c[i]! & ~keep) { c[i] = c[i]! & keep; changed = true }
        return
      }
      for (let k = start; k < ds.length; k++) combos(k + 1, [...picked, ds[k]!])
    }
    combos(0, [])
  }
  return changed
}

function intersections(c: number[], g: number[]): boolean {
  let changed = false
  for (let b = 0; b < 9; b++) {
    const box = UNITS[18 + b]!
    for (let d = 1; d <= 9; d++) {
      const spots = box.filter((i) => !g[i] && c[i]! & bit(d))
      if (spots.length < 2) continue
      for (const [key, unitBase] of [[ROW, 0], [COL, 9]] as const) {
        const k = key(spots[0]!)
        if (spots.every((i) => key(i) === k)) {
          for (const i of UNITS[unitBase + k]!) if (!box.includes(i) && !g[i] && c[i]! & bit(d)) { c[i] = c[i]! & ~bit(d); changed = true }
        }
      }
    }
  }
  // box-line reduction
  for (let u = 0; u < 18; u++) for (let d = 1; d <= 9; d++) {
    const spots = UNITS[u]!.filter((i) => !g[i] && c[i]! & bit(d))
    if (spots.length < 2) continue
    const bx = BOX(spots[0]!)
    if (spots.every((i) => BOX(i) === bx)) {
      for (const i of UNITS[18 + bx]!) if (!UNITS[u]!.includes(i) && !g[i] && c[i]! & bit(d)) { c[i] = c[i]! & ~bit(d); changed = true }
    }
  }
  return changed
}

function fish(c: number[], g: number[], size: number): boolean {
  let changed = false
  for (const [base, crossKey] of [[0, COL], [9, ROW]] as const) {
    for (let d = 1; d <= 9; d++) {
      const lines = Array.from({ length: 9 }, (_, k) => UNITS[base + k]!.filter((i) => !g[i] && c[i]! & bit(d)).map(crossKey))
      const eligible = lines.map((l, k) => [k, l] as const).filter(([, l]) => l.length >= 2 && l.length <= size)
      const pick = (start: number, picked: (readonly [number, number[]])[]): void => {
        if (picked.length === size) {
          const cover = new Set(picked.flatMap(([, l]) => l))
          if (cover.size !== size) return
          const rowsUsed = new Set(picked.map(([k]) => k))
          for (const x of cover) {
            const crossBase = base === 0 ? 9 : 0
            for (const i of UNITS[crossBase + x]!) {
              const lineIdx = base === 0 ? ROW(i) : COL(i)
              if (!rowsUsed.has(lineIdx) && !g[i] && c[i]! & bit(d)) { c[i] = c[i]! & ~bit(d); changed = true }
            }
          }
          return
        }
        for (let k = start; k < eligible.length; k++) pick(k + 1, [...picked, eligible[k]!])
      }
      pick(0, [])
    }
  }
  return changed
}

function xyWing(c: number[], g: number[]): boolean {
  const bivalue = Array.from({ length: 81 }, (_, i) => i).filter((i) => !g[i] && popcount(c[i]!) === 2)
  for (const pivot of bivalue) {
    const [x, y] = digitsOf(c[pivot]!) as [number, number]
    const wings = PEERS[pivot]!.filter((p) => bivalue.includes(p))
    for (const a of wings) for (const b of wings) {
      if (a >= b) continue
      const ma = c[a]!, mb = c[b]!
      // a = {x,z}, b = {y,z}
      for (const [p, q] of [[x, y], [y, x]] as const) {
        if (!(ma & bit(p)) || !(mb & bit(q))) continue
        const za = ma & ~bit(p), zb = mb & ~bit(q)
        if (za !== zb || popcount(za) !== 1 || za & c[pivot]!) continue
        let changed = false
        for (const i of PEERS[a]!) if (i !== b && i !== pivot && PEERS[b]!.includes(i) && !g[i] && c[i]! & za) { c[i] = c[i]! & ~za; changed = true }
        if (changed) return true
      }
    }
  }
  return false
}

function xyzWing(c: number[], g: number[]): boolean {
  for (let pivot = 0; pivot < 81; pivot++) {
    if (g[pivot] || popcount(c[pivot]!) !== 3) continue
    const pm = c[pivot]!
    const wings = PEERS[pivot]!.filter((p) => !g[p] && popcount(c[p]!) === 2 && (c[p]! & pm) === c[p]!)
    for (const a of wings) for (const b of wings) {
      if (a >= b || (c[a]! | c[b]!) !== pm) continue
      const z = c[a]! & c[b]!
      if (popcount(z) !== 1) continue
      let changed = false
      for (const i of PEERS[pivot]!) {
        if (i === a || i === b || g[i] || !(c[i]! & z)) continue
        if (PEERS[a]!.includes(i) && PEERS[b]!.includes(i)) { c[i] = c[i]! & ~z; changed = true }
      }
      if (changed) return true
    }
  }
  return false
}

/** Simple coloring: chains of conjugate pairs for one digit. Two cells of one color that see each other
 *  make that color false (wrap); a cell that sees both colors can't hold the digit (trap). */
function simpleColoring(c: number[], g: number[]): boolean {
  for (let d = 1; d <= 9; d++) {
    const b = bit(d)
    const links: number[][] = Array.from({ length: 81 }, () => [])
    for (const unit of UNITS) {
      const spots = unit.filter((i) => !g[i] && c[i]! & b)
      if (spots.length === 2) { links[spots[0]!]!.push(spots[1]!); links[spots[1]!]!.push(spots[0]!) }
    }
    const color = new Array<number>(81).fill(-1)
    for (let start = 0; start < 81; start++) {
      if (color[start] !== -1 || !links[start]!.length) continue
      const comp: number[] = []
      const stack = [start]
      color[start] = 0
      while (stack.length) {
        const i = stack.pop()!
        comp.push(i)
        for (const j of links[i]!) if (color[j] === -1) { color[j] = 1 - color[i]!; stack.push(j) }
      }
      if (comp.length < 4) continue
      const sees = (i: number, j: number): boolean => PEERS[i]!.includes(j)
      for (const col of [0, 1]) {
        const same = comp.filter((i) => color[i] === col)
        if (same.some((i, k) => same.slice(k + 1).some((j) => sees(i, j)))) {
          for (const i of same) c[i] = c[i]! & ~b
          return true
        }
      }
      let changed = false
      for (let i = 0; i < 81; i++) {
        if (g[i] || !(c[i]! & b) || comp.includes(i)) continue
        const seen = comp.filter((j) => sees(i, j))
        if (seen.some((j) => color[j] === 0) && seen.some((j) => color[j] === 1)) { c[i] = c[i]! & ~b; changed = true }
      }
      if (changed) return true
    }
  }
  return false
}

/** Hardest tier (1–5) needed to solve with the techniques above, or 0 if they cannot solve it.
 *  Tier 5 (Evil) adds jellyfish, XYZ-wing and simple coloring; anything needing more is rejected. */
export function grade(puzzle: number[]): number {
  const g = puzzle.slice()
  const c = candidates(g)
  let tier = 1
  for (let guard = 0; guard < 2000; guard++) {
    if (g.every((v) => v)) return tier
    if (singles(g, c)) continue
    if (nakedSubset(c, g, 2) || hiddenSubset(c, g, 2) || intersections(c, g)) { tier = Math.max(tier, 2); continue }
    if (nakedSubset(c, g, 3) || hiddenSubset(c, g, 3) || fish(c, g, 2)) { tier = Math.max(tier, 3); continue }
    if (fish(c, g, 3) || xyWing(c, g)) { tier = Math.max(tier, 4); continue }
    if (fish(c, g, 4) || xyzWing(c, g) || simpleColoring(c, g)) { tier = Math.max(tier, 5); continue }
    return 0
  }
  return 0
}

export const TIER_LEVEL: Record<number, Level> = { 1: "easy", 2: "medium", 3: "hard", 4: "expert", 5: "evil" }

export interface Puzzle { id: string; level: Level; puzzle: string; solution: string; givens: number }

/** Generates one puzzle from a seed; returns null if its grade lands outside every level. */
export function generate(seed: string, minGivens = 17): Puzzle | null {
  const rand = seeded(`sudoku:${seed}`)
  const solution = new Array(81).fill(0)
  countSolutions(new Array(81).fill(0), 1, solution, rand)
  const grid = solution.slice()
  // Remove cells in random symmetric pairs while the solution stays unique.
  const order = shuffle(Array.from({ length: 41 }, (_, i) => i), rand)
  let givens = 81
  for (const i of order) {
    const j = 80 - i
    const keep = [grid[i]!, grid[j]!]
    grid[i] = 0; grid[j] = 0
    const removed = i === j ? 1 : 2
    if (givens - removed < minGivens || countSolutions(grid, 2) !== 1) { grid[i] = keep[0]!; grid[j] = keep[1]! }
    else givens -= removed
  }
  const tier = grade(grid)
  if (!tier) return null
  return { id: seed, level: TIER_LEVEL[tier]!, puzzle: toStr(grid), solution: toStr(solution), givens }
}

/** Raises the givens of an over-hard puzzle toward an easier level by restoring solution cells. */
export function easeTo(p: Puzzle, target: Level, seed: string): Puzzle | null {
  const rand = seeded(`ease:${seed}`)
  const grid = parse(p.puzzle)
  const sol = parse(p.solution)
  const empties = shuffle(grid.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0), rand)
  const want = LEVELS.indexOf(target) + 1
  for (const i of empties) {
    const t = grade(grid)
    if (t === want) return { ...p, level: target, puzzle: toStr(grid), givens: grid.filter(Boolean).length }
    if (t !== 0 && t < want) return null
    grid[i] = sol[i]!
  }
  return null
}

/**
 * Minimum givens per level. Hypothesis, not sourced: a technique tier says how hard the logic is, but a
 * singles-only grid with 24 givens still feels long. These floors keep Easy approachable.
 */
export const MIN_GIVENS: Record<Level, number> = { easy: 36, medium: 30, hard: 26, expert: 22, evil: 22 }

/** Restores solution cells until the level's minimum givens is met, without changing the level. */
export function padGivens(p: Puzzle, seed: string): Puzzle {
  const rand = seeded(`pad:${seed}`)
  const grid = parse(p.puzzle)
  const sol = parse(p.solution)
  const want = LEVELS.indexOf(p.level) + 1
  const empties = shuffle(grid.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0), rand)
  for (const i of empties) {
    if (grid.filter(Boolean).length >= MIN_GIVENS[p.level]) break
    grid[i] = sol[i]!
    if (grade(grid) !== want) grid[i] = 0
  }
  return { ...p, puzzle: toStr(grid), givens: grid.filter(Boolean).length }
}
